'use client';

import Image from 'next/image';
import { Facebook, Link2, Linkedin, Phone, Podcast, Twitter, Youtube } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@kit/ui/tooltip';

import type { SocialBlock } from '../../_lib/schema/landing-config.schema';
import { isSafeLandingSocialHref } from '../../_lib/landing-social-links';
import { type LandingRenderContext, landingImageUrl, pickLocalized } from './landing-context';
import { SocialBrandIcon } from './social-brand-icon';
import styles from './social-block.module.css';

const icons = { facebook: Facebook, linkedin: Linkedin, youtube: Youtube, x: Twitter, spotify: Podcast, phone: Phone, custom: Link2 };
const names = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', youtube: 'YouTube', x: 'X', spotify: 'Spotify', whatsapp: 'WhatsApp', android: 'Android app', ios: 'iOS app', phone: 'Contact number', custom: 'Link' };

export function SocialBlockView({ block, ctx }: { block: SocialBlock; ctx: LandingRenderContext }) {
  const links = block.links.filter((link) => isSafeLandingSocialHref(link.platform, link.href));
  if (!links.length) return null;
  const title = pickLocalized(block.title, block.title_ar, ctx.locale);
  return <section className={styles.section} data-test="landing-social-section" data-align={block.align ?? 'center'} aria-label={title || (ctx.locale.startsWith('ar') ? 'وسائل التواصل الاجتماعي' : 'Social media')}>
    {title ? <h2>{title}</h2> : null}
    <TooltipProvider delayDuration={150}><ul className={styles.links}>
      {links.map((link, index) => {
        const Icon = link.platform in icons ? icons[link.platform as keyof typeof icons] : null;
        const label = pickLocalized(link.label, link.label_ar, ctx.locale) || names[link.platform];
        const anchor = <a href={link.href} target="_blank" rel="noopener noreferrer" aria-label={`${label} — ${ctx.locale.startsWith('ar') ? 'يفتح في علامة تبويب جديدة' : 'opens in a new tab'}`}>
          <span className={styles.icon}>
            {link.imageKey ? <Image src={landingImageUrl(ctx, link.imageKey)} alt="" width={28} height={28} unoptimized /> : Icon ? <Icon size={22} aria-hidden="true" /> : <SocialBrandIcon platform={link.platform as 'instagram' | 'tiktok' | 'whatsapp' | 'android' | 'ios'} />}
          </span>
          <span className={link.labelDisplay === 'visible' ? styles.label : styles.touchLabel} aria-hidden="true" dir="auto">{label}</span>
        </a>;
        return <li key={`${link.platform}-${index}`} data-label-display={link.labelDisplay ?? 'hover'}>
          {link.labelDisplay === 'visible' ? anchor : <Tooltip><TooltipTrigger asChild>{anchor}</TooltipTrigger><TooltipContent className={styles.tooltip} side="top">{label}</TooltipContent></Tooltip>}
        </li>;
      })}
    </ul></TooltipProvider>
  </section>;
}
